import { useMemo, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@tasama/i18n';
import {
  DateInput,
  Dropdown,
  SearchInputSelection,
  OnboardingHeader,
  InputWithLabel,
  Grid,
  cn,
  CountryFlag,
} from '@tasama/ui';
import type { DropdownOption } from '@tasama/ui';
import {
  getIndustrySectorOptions,
  getEmployeeCountOptions,
  getAnnualRevenueOptions,
  getInvestmentRaisedOptions,
  getGccCountries,
  COMPANY_TYPES,
  getCompanyDetailsSchema,
  getCompanyDetailsDefaultValues,
  type ExtendedCompanyDetailsFormValues,
} from '../schemas/company-details.schema';
import { useCompanyDetailsStore } from '../stores/company-details.store';
import type { CompanyDetailsStore } from '../stores/company-details.store';
import { useCompanyTypeFormData } from '../../company-type/stores/company-type-store';
import { useCountries } from '../hooks/use-countries.hook';
import { getCountriesByCompanyType, toAsciiDigits } from '@tasama/utils';

export function CompanyDetailsPage() {
  const { t } = useTranslation(['onboarding', 'common']);
  const title = t('companyDetails.title');
  const description = t('companyDetails.description');

  const companyTypeData = useCompanyTypeFormData();
  const { countries, isLoading: countriesLoading } = useCountries();

  const industrySectorOptions = useMemo(() => getIndustrySectorOptions(t), [t]);
  const employeeCountOptions = useMemo(() => getEmployeeCountOptions(t), [t]);
  const annualRevenueOptions = useMemo(() => getAnnualRevenueOptions(t), [t]);
  const investmentRaisedOptions = useMemo(
    () => getInvestmentRaisedOptions(t),
    [t]
  );
  const gccCountries = useMemo(() => getGccCountries(t), [t]);

  const schema = useMemo(
    () => getCompanyDetailsSchema(companyTypeData, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyTypeData.companyType, companyTypeData.companyStatus, t]
  );

  // Read persisted data once — restores fields when navigating back to this step
  const storedFormData = useCompanyDetailsStore.getState().formData;

  const defaultValues = useMemo(
    () =>
      ({
        ...getCompanyDetailsDefaultValues(companyTypeData),
        ...storedFormData,
      }) as ExtendedCompanyDetailsFormValues,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyTypeData.companyType, companyTypeData.companyStatus]
  );

  const {
    watch,
    control,
    setValue,
    setError,
    formState: { errors, isValid },
  } = useForm<ExtendedCompanyDetailsFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any),
    mode: 'onTouched',
    defaultValues,
  });

  // Coordinator store sync — keeps use-step-validation and use-onboarding-payload working
  const setStoreValidity = useCompanyDetailsStore(
    (s: CompanyDetailsStore) => s.setValidity
  );
  const setStoreFormData = useCompanyDetailsStore(
    (s: CompanyDetailsStore) => s.setFormData
  );
  const pendingApiError = useCompanyDetailsStore(
    (s: CompanyDetailsStore) => s.pendingApiError
  );
  const clearApiError = useCompanyDetailsStore(
    (s: CompanyDetailsStore) => s.clearApiError
  );

  useEffect(() => {
    setStoreValidity(isValid);
  }, [isValid, setStoreValidity]);

  // Bridge API field errors into RHF
  useEffect(() => {
    if (pendingApiError) {
      setError(
        pendingApiError.field as keyof ExtendedCompanyDetailsFormValues,
        {
          type: 'server',
          message: pendingApiError.message,
        }
      );
      clearApiError();
    }
  }, [pendingApiError, setError, clearApiError]);

  useEffect(() => {
    const subscription = watch((values) => {
      setStoreFormData(values as Partial<ExtendedCompanyDetailsFormValues>);
    });
    return () => subscription.unsubscribe();
  }, [watch, setStoreFormData]);

  const formValues = useWatch({ control });

  const handleFieldChange = (
    field: keyof ExtendedCompanyDetailsFormValues,
    value: string
  ) => {
    setValue(field, value as any, { shouldValidate: true, shouldTouch: true });
  };

  const isInternational =
    companyTypeData.companyType === COMPANY_TYPES.INTERNATIONAL;

  const getCountriesList = (): DropdownOption[] =>
    getCountriesByCompanyType(
      isInternational,
      Array.isArray(countries) ? countries : [],
      gccCountries
    ).map((country) => ({
      value: country.value,
      label: country.label,
      icon: <CountryFlag countryCode={country.value} />,
    }));

  const shouldRenderField = (
    fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => {
    const { companyType, companyStatus } = companyTypeData;

    switch (fieldName) {
      case 'industrySector':
        return (
          companyType === COMPANY_TYPES.INTERNATIONAL ||
          companyType === COMPANY_TYPES.KSA_PRIVATE ||
          (companyType === COMPANY_TYPES.PIF_OWNED &&
            companyStatus === 'PIF_ESTABLISHED')
        );
      case 'countryOfHeadquarter':
        return companyType === COMPANY_TYPES.INTERNATIONAL;
      case 'country':
        return companyType !== COMPANY_TYPES.INTERNATIONAL;
      case 'numberOfEmployees':
        return (
          companyType === COMPANY_TYPES.INTERNATIONAL ||
          companyType === COMPANY_TYPES.KSA_PRIVATE
        );
      case 'annualRevenue':
        return (
          companyType === COMPANY_TYPES.INTERNATIONAL ||
          companyType === COMPANY_TYPES.KSA_PRIVATE
        );
      case 'totalInvestmentRaised':
        return companyType === COMPANY_TYPES.INTERNATIONAL;
      default:
        return true;
    }
  };

  const isFieldRequired = (
    fieldName: keyof ExtendedCompanyDetailsFormValues
  ) => {
    const { companyType, companyStatus } = companyTypeData;

    const baseRequired = [
      'companyName',
      'registrationNumber',
      'registrationStartDate',
    ];
    if (baseRequired.includes(fieldName)) return true;

    if (companyType === COMPANY_TYPES.INTERNATIONAL) {
      return [
        'industrySector',
        'countryOfHeadquarter',
        'addressLine1',
        'city',
        'numberOfEmployees',
        'annualRevenue',
        'totalInvestmentRaised',
      ].includes(fieldName);
    }

    if (companyType === COMPANY_TYPES.KSA_PRIVATE) {
      return ['industrySector', 'country'].includes(fieldName);
    }

    if (
      companyType === COMPANY_TYPES.PIF_OWNED &&
      companyStatus === 'PIF_ESTABLISHED'
    ) {
      return ['industrySector', 'country'].includes(fieldName);
    }

    return ['country'].includes(fieldName);
  };

  return (
    <div className="space-y-6">
      <OnboardingHeader
        size="lg"
        title={title}
        description={description}
        icon="FileText"
        testID="details-header"
      />

      <form className="space-y-6" noValidate>
        <Grid className="grid-cols-1 gap-4">
          <InputWithLabel
            id="companyName"
            label={t('common:companyForm.registeredCompanyName')}
            required
            placeholder={t('common:companyForm.companyNamePlaceholder')}
            value={formValues.companyName || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('companyName', e.target.value)
            }
            error={!!errors.companyName}
            errorMessage={errors.companyName?.message}
            testID="company-name-field"
          />
        </Grid>

        <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
          <InputWithLabel
            id="registrationNumber"
            label={t('common:companyForm.companyRegistrationNumber')}
            required
            placeholder={t('common:companyForm.crNumberPlaceholder')}
            value={formValues.registrationNumber || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange(
                'registrationNumber',
                toAsciiDigits(e.target.value)
              )
            }
            error={!!errors.registrationNumber}
            errorMessage={errors.registrationNumber?.message}
            testID="registration-number-field"
          />
          <DateInput
            id="registrationStartDate"
            label={t('common:companyForm.registrationStartDate')}
            required
            value={formValues.registrationStartDate || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('registrationStartDate', e?.target?.value ?? '')
            }
            placeholder={t('dateInput.placeholder', { ns: 'common' })}
            className={cn(
              formValues.registrationStartDate ? 'date-input--has-value' : ''
            )}
            error={!!errors.registrationStartDate}
            errorMessage={errors.registrationStartDate?.message}
            testID="registration-date-field"
            disabledDays={{ after: new Date() }}
            endMonth={new Date()}
            yearRange={new Date().getFullYear() - 1950}
          />
        </Grid>

        <Grid className="grid-cols-1 gap-4">
          <InputWithLabel
            id="website"
            label={t('common:companyForm.companyWebsite')}
            placeholder={t('common:companyForm.enterWebsiteUrl')}
            value={formValues.website || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('website', e.target.value)
            }
            error={!!errors.website}
            errorMessage={errors.website?.message}
            testID="website-field"
          />
        </Grid>

        {shouldRenderField('industrySector') && (
          <Grid className="grid-cols-1 gap-4">
            <Dropdown
              id="industrySector"
              label={t('common:companyForm.industrySector')}
              placeholder={t('common:companyForm.industrySectorPlaceholder')}
              options={industrySectorOptions}
              value={formValues.industrySector || ''}
              onChange={(value: string) =>
                handleFieldChange('industrySector', value)
              }
              error={!!errors.industrySector}
              errorMessage={errors.industrySector?.message}
              testID="industry-sector-field"
              showLeftIcon={false}
            />
          </Grid>
        )}

        {shouldRenderField('countryOfHeadquarter') && (
          <Grid className="grid-cols-1 gap-4">
            {companyTypeData.companyType === COMPANY_TYPES.INTERNATIONAL ? (
              <SearchInputSelection
                id="countryOfHeadquarter"
                label={t('companyDetails.fields.countryOfHeadquarter.label')}
                placeholder={t('common:companyForm.countryPlaceholder')}
                options={getCountriesList()}
                value={formValues.countryOfHeadquarter || ''}
                onChange={(value: string) =>
                  handleFieldChange('countryOfHeadquarter', value)
                }
                error={!!errors.countryOfHeadquarter}
                errorMessage={errors.countryOfHeadquarter?.message}
                testID="country-headquarter-field"
                showLeftIcon={false}
                disabled={countriesLoading}
              />
            ) : null}
          </Grid>
        )}

        {shouldRenderField('country') && (
          <Grid className="grid-cols-1 gap-4">
            <SearchInputSelection
              id="country"
              label={t('common:companyForm.countryOfRegistration')}
              placeholder={t('common:companyForm.countryPlaceholder')}
              options={getCountriesList()}
              value={formValues.country || ''}
              onChange={(value: string) => handleFieldChange('country', value)}
              error={!!errors.country}
              errorMessage={errors.country?.message}
              testID="country-field"
              showLeftIcon={false}
              disabled
            />
          </Grid>
        )}

        <Grid className="grid-cols-1 gap-4">
          <InputWithLabel
            id="addressLine1"
            label={
              companyTypeData.companyType === COMPANY_TYPES.INTERNATIONAL
                ? t('companyDetails.fields.addressLine1.labelInternational')
                : t('common:companyForm.registeredCompanyAddress')
            }
            placeholder={t('common:companyForm.enterAddress')}
            value={formValues.addressLine1 || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('addressLine1', e.target.value)
            }
            error={!!errors.addressLine1}
            errorMessage={errors.addressLine1?.message}
            testID="address-field"
            required={isFieldRequired('addressLine1')}
          />
        </Grid>

        <Grid className="grid-cols-1 md:grid-cols-2 gap-4">
          <InputWithLabel
            id="city"
            label={t('common:companyForm.city')}
            placeholder={t('common:companyForm.enterCity')}
            value={formValues.city || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('city', e.target.value)
            }
            error={!!errors.city}
            errorMessage={errors.city?.message}
            testID="city-field"
            required={isFieldRequired('city')}
          />
          <InputWithLabel
            id="postcode"
            label={t('common:companyForm.postcode')}
            placeholder={t('common:companyForm.enterPostcode')}
            value={formValues.postcode || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('postcode', toAsciiDigits(e.target.value))
            }
            error={!!errors.postcode}
            errorMessage={errors.postcode?.message}
            testID="postcode-field"
            required={isFieldRequired('postcode')}
          />
        </Grid>

        {shouldRenderField('numberOfEmployees') && (
          <Grid
            className={`grid-cols-1 ${shouldRenderField('annualRevenue') ? 'md:grid-cols-2' : ''} gap-4`}
          >
            <Dropdown
              id="numberOfEmployees"
              label={t('common:companyForm.numberOfEmployees')}
              placeholder={t('common:companyForm.numberOfEmployeesPlaceholder')}
              options={employeeCountOptions}
              value={formValues.numberOfEmployees || ''}
              onChange={(value: string) =>
                handleFieldChange('numberOfEmployees', value)
              }
              error={!!errors.numberOfEmployees}
              errorMessage={errors.numberOfEmployees?.message}
              testID="number-of-employees-field"
              showLeftIcon={false}
              required={isFieldRequired('numberOfEmployees')}
            />
            {shouldRenderField('annualRevenue') && (
              <Dropdown
                id="annualRevenue"
                label={t('common:companyForm.annualRevenue')}
                placeholder={t('common:companyForm.annualRevenuePlaceholder')}
                options={annualRevenueOptions}
                value={formValues.annualRevenue || ''}
                onChange={(value: string) =>
                  handleFieldChange('annualRevenue', value)
                }
                error={!!errors.annualRevenue}
                errorMessage={errors.annualRevenue?.message}
                testID="annual-revenue-field"
                showLeftIcon={false}
                required={isFieldRequired('annualRevenue')}
              />
            )}
          </Grid>
        )}

        {shouldRenderField('totalInvestmentRaised') && (
          <Grid className="grid-cols-1 gap-4">
            <Dropdown
              id="totalInvestmentRaised"
              label={t('common:companyForm.totalInvestmentRaised')}
              placeholder={t(
                'common:companyForm.totalInvestmentRaisedPlaceholder'
              )}
              options={investmentRaisedOptions}
              value={formValues.totalInvestmentRaised || ''}
              onChange={(value: string) =>
                handleFieldChange('totalInvestmentRaised', value)
              }
              error={!!errors.totalInvestmentRaised}
              errorMessage={errors.totalInvestmentRaised?.message}
              testID="investment-raised-field"
              showLeftIcon={false}
            />
          </Grid>
        )}
      </form>
    </div>
  );
}

export default CompanyDetailsPage;
